export const REDACTED = '[REDACTED]';

// Matches the name/id/placeholder/autocomplete/class of a form control that
// holds something we must never ship to the helpdesk.
const SENSITIVE_FIELD_PATTERN =
  /card|cvv|cvc|securitycode|security-code|password|passwd|passport|ssn|social|taxid|tax-id|dob|birth|email|phone|tel|mobile|address|street|zip|postal|account|iban|routing|licen[cs]e|document/i;

// Query parameters that can carry an auth token or personal data. RSI signs in
// through a token in the URL, so the raw href is not safe to forward as-is.
const SENSITIVE_PARAM_PATTERN =
  /token|password|passwd|secret|key|auth|session|email|phone|card|cvv|ssn|signature/i;

// Opt-in hooks so a feature can flag its own markup without touching this file.
const REDACT_ATTRIBUTES = ['data-issue-report-redact', 'data-sensitive'];

const MIN_PAN_DIGITS = 13;
const MAX_PAN_DIGITS = 19;
const DIGIT_RUN = /\d(?:[\d \-]{11,25})\d/g;

const TEN = 10;
const TWO = 2;
const NINE = 9;

// Luhn check, so only sequences that really look like a card number are scrubbed
// and ordinary long numbers (booking ids, phone-less digits) are left readable.
const isLuhnValid = (digits: string): boolean => {
  let sum = 0;
  let double = false;

  for (let i = digits.length - 1; i >= 0; i -= 1) {
    let value = Number(digits[i]);

    if (double) {
      value *= TWO;
      if (value > NINE) {
        value -= NINE;
      }
    }

    sum += value;
    double = !double;
  }

  return sum % TEN === 0;
};

export const scrubCardNumbers = (text: string): string =>
  text.replace(DIGIT_RUN, (match) => {
    const digits = match.replace(/\D/g, '');

    if (digits.length < MIN_PAN_DIGITS || digits.length > MAX_PAN_DIGITS) {
      return match;
    }

    return isLuhnValid(digits) ? REDACTED : match;
  });

// Keeps the page identity (path) but never forwards a token or personal value.
export const sanitizeUrl = (href: string): string => {
  try {
    const url = new URL(href);

    url.searchParams.forEach((_, key) => {
      if (SENSITIVE_PARAM_PATTERN.test(key)) {
        url.searchParams.set(key, REDACTED);
      }
    });

    return url.toString();
  } catch (e) {
    // Not parseable: drop the query string rather than risk forwarding it.
    return href.split('?')[0];
  }
};

// Any control inside one of these blocks is masked regardless of its own
// attributes. Two deliberate exclusions: `credit-line` (a financing banner, not
// card data, which helpdesk needs to see) and a bare `holder`, which would also
// match `placeholder` and mask half the page.
const SENSITIVE_CONTAINER_PATTERN =
  /payment|billing|credit-?card|cardholder|guest-info|guest-form|travel(?:l)?er|passenger/i;

// Ant Design renders <Select> as a div, not a native <select>, so the chosen
// value (e.g. card expiry month) lives in this element rather than in a value.
const ANTD_SELECT_VALUE = '.ant-select-selection-item';

const describes = (element: Element): string =>
  ['name', 'id', 'placeholder', 'autocomplete', 'aria-label', 'class', 'data-testid']
    .map((attribute) => element.getAttribute(attribute) ?? '')
    .join(' ');

const isSensitiveControl = (element: Element): boolean => {
  if ((element.getAttribute('type') ?? '').toLowerCase() === 'password') {
    return true;
  }

  return SENSITIVE_FIELD_PATTERN.test(describes(element));
};

const maskControl = (element: Element): void => {
  const tag = element.tagName.toLowerCase();

  if (tag === 'select') {
    const selected = element.querySelector('option[selected]') ?? element.querySelector('option');

    if (selected) {
      selected.textContent = REDACTED;
    }
    return;
  }

  if (tag === 'textarea') {
    element.textContent = REDACTED;
    return;
  }

  // html2canvas rasterises an input from its value, so both need overwriting.
  element.setAttribute('value', REDACTED);
  (element as HTMLInputElement).value = REDACTED;
};

const maskSelectedValue = (element: Element): void => {
  element.textContent = REDACTED;
  element.setAttribute('title', REDACTED);
};

// Masks every value inside a payment/billing block while leaving its labels and
// validation messages readable, which is what helpdesk needs to reproduce.
const maskValuesWithin = (root: Element): void => {
  root.querySelectorAll('input, textarea, select').forEach(maskControl);
  root.querySelectorAll(ANTD_SELECT_VALUE).forEach(maskSelectedValue);
};

// Runs against html2canvas's cloned document: the live page is never modified,
// so the member sees nothing change while the capture is taken.
export const redactDocument = (doc: Document): void => {
  doc.querySelectorAll('input, textarea, select').forEach((element) => {
    if (isSensitiveControl(element)) {
      maskControl(element);
    }
  });

  doc.querySelectorAll('[class], [id]').forEach((element) => {
    const identity = `${element.getAttribute('class') ?? ''} ${element.getAttribute('id') ?? ''}`;

    if (SENSITIVE_CONTAINER_PATTERN.test(identity)) {
      maskValuesWithin(element);
    }
  });

  REDACT_ATTRIBUTES.forEach((attribute) => {
    doc.querySelectorAll(`[${attribute}]`).forEach((element) => {
      element.textContent = REDACTED;
    });
  });

  // Catches a card number rendered as plain text (order summaries, confirmations).
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);

  while (walker.nextNode()) {
    const node = walker.currentNode;
    const text = node.nodeValue ?? '';

    if (text.length >= MIN_PAN_DIGITS) {
      const scrubbed = scrubCardNumbers(text);

      if (scrubbed !== text) {
        node.nodeValue = scrubbed;
      }
    }
  }
};
